/**
 * Offerte-PDF uit Gripp downloaden via onze eigen route (22 okt 2026).
 *
 * De oude "PDF"-knop linkte rechtstreeks naar Gripps publieke directpdfurl. Ligt
 * Gripp plat, dan kwam je op een statuspagina van Exact zonder uitleg. Deze route
 * haalt de PDF server-side op en geeft een leesbare fout ("Gripp is tijdelijk niet
 * bereikbaar") als dat niet lukt.
 */
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

/** Offertenummer heeft voorrang; anders het Gripp-id als `id-<id>`. */
export function pdfRef({ offerNumber, offerId } = {}) {
  const nummer = String(offerNumber ?? '').trim();
  if (/^\d+$/.test(nummer)) return nummer;
  const id = String(offerId ?? '').trim();
  if (/^\d+$/.test(id)) return `id-${id}`;
  return null;
}

/** Bestandsnaam uit de Content-Disposition (filename* wint), anders een nette terugval. */
export function bestandsnaamUit(header, terugval) {
  const kop = String(header || '');
  const utf = kop.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf) {
    try { return decodeURIComponent(utf[1].trim()); } catch { /* val door */ }
  }
  const plat = kop.match(/filename="([^"]+)"/i);
  return plat ? plat[1] : terugval;
}

/** Zelfde vorm als de server: `Offerte 9866 - Onderwerp.pdf`. De header is
 *  cross-origin niet leesbaar (CORS stelt hem niet open), dus dit is meestal de naam. */
export function standaardBestandsnaam(nummer, onderwerp) {
  let titel = String(onderwerp || '').replace(/[\\/:*?"<>|\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ').trim().replace(/^[ .]+|[ .]+$/g, '');
  // Afkappen op codepunten: .slice() kan een emoji doormidden knippen (URIError).
  titel = Array.from(titel).slice(0, 80).join('').trim();
  return titel ? `Offerte ${nummer} - ${titel}.pdf` : `Offerte ${nummer}.pdf`;
}

export async function downloadOffertePdf({ offerNumber, offerId, subject } = {}) {
  const ref = pdfRef({ offerNumber, offerId });
  if (!ref) { toast.error('Geen offertenummer bekend om de PDF op te halen'); return false; }
  const toastId = toast.loading('PDF ophalen uit Gripp…');
  try {
    const r = await authFetch(`${BACKEND_URL}/Api/offertes/${encodeURIComponent(ref)}/pdf`);
    if (!r.ok) {
      let detail = '';
      try { const d = await r.json(); detail = d?.detail || d?.error || ''; } catch { /* geen JSON */ }
      throw new Error(typeof detail === 'string' && detail ? detail : `PDF ophalen mislukt (${r.status})`);
    }
    const blob = await r.blob();
    const naam = bestandsnaamUit(r.headers.get('content-disposition'),
      standaardBestandsnaam(offerNumber || ref, subject));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = naam;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    toast.success('PDF gedownload', { id: toastId });
    return true;
  } catch (e) {
    toast.error(e?.message || 'PDF ophalen mislukt', { id: toastId });
    return false;
  }
}
